import React, { useRef, useState } from "react";
import worldMapImage from "../assets/MapMiniScen.webp";

/*
 * The interactive areas are detected directly from the colors in the map.
 * This avoids maintaining approximate SVG rectangles and keeps the clickable
 * areas aligned when the map is resized.
 */
const REGION_COLOR_SAMPLES = [
  { key: "north-america", rgb: [7, 109, 209] },
  { key: "central-south-america", rgb: [3, 171, 187] },
  { key: "europe-russia", rgb: [90, 183, 49] },
  { key: "africa-middle-east", rgb: [252, 133, 10] },
  { key: "asean", rgb: [243, 207, 66] },
  { key: "rest-asia-pacific", rgb: [138, 84, 196] },
];

// Maximum RGB distance accepted as part of one of the six regions.
const COLOR_MATCH_THRESHOLD = 90;
const COLOR_MATCH_THRESHOLD_SQUARED = COLOR_MATCH_THRESHOLD ** 2;

const countriesData = {
  NAM: {
    title: "North America",
    subtitle: "Countries included in this region",
    countries: ["Canada", "Mexico", "United States"],
  },
  CSAM: {
    title: "Central and South America",
    subtitle: "Countries included in this region",
    countries: [
      "Argentina",
      "Bahamas",
      "Belize",
      "Bolivia",
      "Brazil",
      "Chile",
      "Colombia",
      "Costa Rica",
      "Cuba",
      "Dominican Republic",
      "Ecuador",
      "El Salvador",
      "Guatemala",
      "Guyana",
      "Haiti",
      "Honduras",
      "Jamaica",
      "Nicaragua",
      "Panama",
      "Paraguay",
      "Peru",
      "Suriname",
      "Trinidad & Tobago",
      "Uruguay",
      "Venezuela"
    ],
  },
  EURUS: {
    title: "Europe and Russia",
    subtitle: "Countries included in this region",
    countries: [
      "Albania",
      "Armenia",
      "Austria",
      "Azerbaijan",
      "Belarus",
      "Belgium",
      "Bosnia & Herzegovina",
      "Bulgaria",
      "Croatia",
      "Cyprus",
      "Czech Republic",
      "Denmark",
      "Estonia",
      "Finland",
      "France",
      "Georgia",
      "Germany",
      "Greece",
      "Hungary",
      "Iceland",
      "Ireland",
      "Italy",
      "Latvia",
      "Lithuania",
      "Luxembourg",
      "North Macedonia",
      "Malta",
      "Moldova",
      "Netherlands",
      "Norway",
      "Poland",
      "Portugal",
      "Romania",
      "Russian Federation",
      "Slovakia",
      "Slovenia",
      "Spain",
      "Sweden",
      "Switzerland",
      "Turkey",
      "Ukraine",
      "United Kingdom"
    ],
  },
  AFRME: {
    title: "Africa and Middle East",
    subtitle: "Countries included in this region",
    countries: [
      "Afghanistan",
      "Algeria",
      "Angola",
      "Benin",
      "Botswana",
      "Burkina Faso",
      "Cameroon",
      "Cape Verde",
      "Central African Republic",
      "Chad",
      "Congo Republic",
      "Côte d'Ivoire",
      "Djibouti",
      "Egypt",
      "Ethiopia",
      "Gabon",
      "Gambia",
      "Ghana",
      "Guinea",
      "Guinea-Bissau",
      "Iran",
      "Iraq",
      "Israel",
      "Jordan",
      "Kenya",
      "Kuwait",
      "Lebanon",
      "Lesotho",
      "Liberia",
      "Madagascar",
      "Malawi",
      "Mali",
      "Mauritania",
      "Mauritius",
      "Morocco",
      "Mozambique",
      "Namibia",
      "Niger",
      "Nigeria",
      "Oman",
      "Rwanda",
      "Saudi Arabia",
      "Senegal",
      "Sierra Leone",
      "South Africa",
      "Tanzania",
      "Togo",
      "Tunisia",
      "Uganda",
      "United Arab Emirates",
      "Yemen",
      "Zambia",
      "Zimbabwe"
    ],
  },
  ASEAN: {
    title: "Southeast Asian Nations",
    subtitle: "ASEAN member states included in this region",
    countries: [
      "Brunei",
      "Cambodia",
      "Indonesia",
      "Laos",
      "Malaysia",
      "Myanmar",
      "Philippines",
      "Singapore",
      "Thailand",
      "Timor-Leste",
      "Vietnam"
    ],
  },
  RASP: {
    title: "Rest of Asia and Pacific",
    subtitle: "Countries included in this region, excluding ASEAN",
    countries: [
      "Australia",
      "Bangladesh",
      "China",
      "Fiji Islands",
      "French Polynesia",
      "India",
      "Japan",
      "Kazakhstan",
      "Korea DPR",
      "Korea Republic",
      "Kyrgyzstan",
      "Mongolia",
      "Nepal",
      "New Zealand",
      "New Caledonia",
      "Pakistan",
      "Samoa",
      "Solomon Islands",
      "Sri Lanka",
      "Tajikistan",
      "Turkmenistan",
      "Uzbekistan",
      "Vanuatu"
    ],
  },
};

const regionData = {
  "north-america": {
    name: "North America (NAM)",
    color: "#076DD1",
    description:
      "A continent with diverse landscapes, from Arctic tundra to tropical rainforests.",
    stats: [
      ["Population", "497 million"],
      ["Area", "19.9 million km²"],
    ],
    countriesKey: "NAM",
  },
  "central-south-america": {
    name: "Central and South America (CSAM)",
    color: "#03ABBB",
    description:
      "A vibrant region known for its biodiversity, cultural diversity, and economic growth.",
    stats: [
      ["Population", "658 million"],
      ["Area", "19.9 million km²"],
    ],
    countriesKey: "CSAM",
  },
  "europe-russia": {
    name: "Europe and Russia (EURUS)",
    color: "#5AB731",
    description:
      "A culturally diverse supercontinent known for its history, art, and technological advancement.",
    stats: [
      ["Population", "1,058 million"],
      ["Area", "28.9 million km²"],
    ],
    countriesKey: "EURUS",
  },
  "africa-middle-east": {
    name: "Africa and Middle East (AFRME)",
    color: "#FC850A",
    description:
      "The second-largest continent with vast savannas, deserts, and rich mineral resources, plus a region with rich cultural heritage and strategic importance.",
    stats: [
      ["Population", "1,414 million"],
      ["Area", "27.5 million km²"],
    ],
    countriesKey: "AFRME",
  },
  asean: {
    name: "Southeast Asian Nations (ASEAN)",
    color: "#F3CF42",
    description:
      "An intergovernmental organization that promotes economic, political, and security cooperation among its member states.",
    stats: [
      ["Population", "668 million"],
      ["Area", "4.5 million km²"],
    ],
    countriesKey: "ASEAN",
  },
  "rest-asia-pacific": {
    name: "Rest of Asia and Pacific (RASP)",
    color: "#8A54C4",
    description:
      "The largest continent featuring diverse cultures, from ancient civilizations to modern economies (excludes ASEAN).",
    stats: [
      ["Population", "3,630 million"],
      ["Area", "28.2 million km²"],
    ],
    countriesKey: "RASP",
  },
};

export default function InteractiveWorldMap({ className = "" }) {
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  const [activeRegion, setActiveRegion] = useState(null);
  const [infoPinned, setInfoPinned] = useState(false);
  const [countriesRegionId, setCountriesRegionId] = useState(null);
  const [countriesPosition, setCountriesPosition] = useState({
    x: -9999,
    y: -9999,
  });
  const [infoPosition, setInfoPosition] = useState({ x: -9999, y: -9999 });

  const currentRegion = activeRegion ? regionData[activeRegion] : null;
  const currentCountries = countriesRegionId
    ? countriesData[countriesRegionId]
    : null;

  const prepareColorCanvas = () => {
    const image = imageRef.current;
    const canvas = canvasRef.current;

    if (!image || !canvas || !image.naturalWidth || !image.naturalHeight) {
      return;
    }

    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
  };

  const getRegionFromPointer = (event) => {
    const image = imageRef.current;
    const canvas = canvasRef.current;

    if (!image || !canvas || !canvas.width || !canvas.height) return null;

    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const relativeX = (event.clientX - rect.left) / rect.width;
    const relativeY = (event.clientY - rect.top) / rect.height;

    if (
      relativeX < 0 ||
      relativeX > 1 ||
      relativeY < 0 ||
      relativeY > 1
    ) {
      return null;
    }

    const pixelX = Math.round(relativeX * (canvas.width - 1));
    const pixelY = Math.round(relativeY * (canvas.height - 1));
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (!context) return null;

    // Search a small neighbourhood so borders and anti-aliased pixels remain interactive.
    const radius = 4;
    let bestRegion = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (let offsetY = -radius; offsetY <= radius; offsetY += 1) {
      for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
        const sampleX = Math.min(
          canvas.width - 1,
          Math.max(0, pixelX + offsetX),
        );
        const sampleY = Math.min(
          canvas.height - 1,
          Math.max(0, pixelY + offsetY),
        );

        const [red, green, blue, alpha] = context.getImageData(
          sampleX,
          sampleY,
          1,
          1,
        ).data;

        if (alpha === 0) continue;

        REGION_COLOR_SAMPLES.forEach(({ key, rgb }) => {
          const redDifference = red - rgb[0];
          const greenDifference = green - rgb[1];
          const blueDifference = blue - rgb[2];
          const distance =
            redDifference * redDifference +
            greenDifference * greenDifference +
            blueDifference * blueDifference;

          if (distance < bestDistance) {
            bestDistance = distance;
            bestRegion = key;
          }
        });
      }
    }

    return bestDistance <= COLOR_MATCH_THRESHOLD_SQUARED ? bestRegion : null;
  };

  const moveInfoCard = (event) => {
    let x = event.clientX + 15;
    let y = event.clientY + 15;
    const cardWidth = 350;
    const cardHeight = 315;

    if (x + cardWidth > window.innerWidth) {
      x = event.clientX - cardWidth - 15;
    }
    if (y + cardHeight > window.innerHeight) {
      y = event.clientY - cardHeight - 15;
    }

    setInfoPosition({ x: Math.max(15, x), y: Math.max(15, y) });
  };

  const moveCountriesCard = (event) => {
    let x = event.clientX + 15;
    let y = event.clientY + 15;
    const cardWidth = 520;
    const cardHeight = 500;

    if (x + cardWidth > window.innerWidth) {
      x = event.clientX - cardWidth - 15;
    }
    if (y + cardHeight > window.innerHeight) {
      y = event.clientY - cardHeight - 15;
    }

    setCountriesPosition({ x: Math.max(15, x), y: Math.max(15, y) });
  };

  const closeAll = () => {
    setActiveRegion(null);
    setInfoPinned(false);
    setCountriesRegionId(null);
  };

  const handleMapPointerMove = (event) => {
    if (infoPinned) return;

    const regionKey = getRegionFromPointer(event);
    setActiveRegion(regionKey);

    if (regionKey) {
      moveInfoCard(event);
    }
  };

  const handleMapClick = (event) => {
    event.stopPropagation();

    const regionKey = getRegionFromPointer(event);

    if (!regionKey) {
      closeAll();
      return;
    }

    setActiveRegion(regionKey);
    setInfoPinned(true);
    setCountriesRegionId(null);
    moveInfoCard(event);
  };

  return (
    <div className={`relative w-full ${className}`} onClick={closeAll}>
      <div
        className="relative bg-white rounded-2xl shadow-lg p-4 md:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-center text-gray-800 font-bold text-xl md:text-2xl mb-6">
          Participating Countries and Regions
        </h2>

        <div className="relative w-full max-w-full mx-auto">
          <div
            className="relative w-full overflow-hidden rounded-xl bg-[#D3EEFE] cursor-pointer"
            style={{ aspectRatio: "1670 / 942" }}
            onMouseMove={handleMapPointerMove}
            onMouseLeave={() => {
              if (!infoPinned) setActiveRegion(null);
            }}
            onClick={handleMapClick}
          >
            <img
              ref={imageRef}
              src={worldMapImage}
              alt="World map divided into six Mini-Scenathon regions"
              className="absolute inset-0 w-full h-full object-contain object-center rounded-xl shadow pointer-events-none select-none"
              onLoad={prepareColorCanvas}
              draggable={false}
            />

            <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
          </div>
        </div>
      </div>

      {currentRegion && (
        <div
          className="fixed z-[1000] bg-white rounded-xl shadow-2xl p-5 w-[340px] max-w-[90vw]"
          style={{ left: infoPosition.x, top: infoPosition.y }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            aria-label="Close region information"
            className="absolute top-2 right-3 text-xl text-gray-400 hover:text-gray-700"
            onClick={() => {
              setActiveRegion(null);
              setInfoPinned(false);
              setCountriesRegionId(null);
            }}
          >
            ×
          </button>

          <div
            className="flex items-center gap-2 text-lg font-bold pb-2 mb-3 pr-5"
            style={{
              color: currentRegion.color,
              borderBottom: `3px solid ${currentRegion.color}`,
            }}
          >
            <span
              className="inline-block w-4 h-4 rounded shrink-0"
              style={{ backgroundColor: currentRegion.color }}
            />
            {currentRegion.name}
          </div>

          <p className="text-sm text-gray-600 mb-3">
            {currentRegion.description}
          </p>

          <div className="space-y-1 text-sm text-gray-700">
            {currentRegion.stats.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4">
                <span>{label}:</span>
                <strong className="text-right">{value}</strong>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="mt-4 w-full bg-green-500 hover:bg-green-600 text-white font-medium py-2 rounded-lg"
            onClick={(event) => {
              event.stopPropagation();
              setCountriesRegionId(currentRegion.countriesKey);
              moveCountriesCard(event);
            }}
          >
            Show Countries
          </button>
        </div>
      )}

      {currentCountries && (
        <div
          className="fixed z-[1001] bg-white rounded-xl shadow-2xl p-5 w-[620px] max-w-[90vw] max-h-[70vh] overflow-y-auto"
          style={{ left: countriesPosition.x, top: countriesPosition.y }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            aria-label="Close country list"
            className="absolute top-2 right-3 text-xl text-gray-400 hover:text-gray-700"
            onClick={() => setCountriesRegionId(null)}
          >
            ×
          </button>

          <h3 className="text-lg font-bold text-gray-900 mb-1 pr-6">
            {currentCountries.title}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {currentCountries.subtitle}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {currentCountries.countries.map((country) => (
              <div
                key={country}
                className="bg-gray-100 rounded px-3 py-2 text-sm text-gray-700"
              >
                {country}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
